import {
  AppName,
  HomeAppBar,
  PageWithDrawer,
  SearchBar,
  SearchParams,
  useOnMount,
} from '@prismamedia/one-components';
import { isValid, parseISO } from 'date-fns';
import { FC, useEffect, useRef, useState, useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import { JsonParam, StringParam, useQueryParam } from 'use-query-params';
import {
  BrandKey,
  ArticleOrderingSearchInput,
  SearchArticlesQuery,
  SearchArticlesQueryVariables,
} from '../../__generated__/queries-web';
import { RecipeOrderByInput } from '../../__generated__/queries-recipe';
import { OneDrawerMenu } from '../../components/AppBar';
import { BrandSwitch } from '../../components/BrandSwitch';
import { auth } from '../../utils/auth';
import { useGlobalBrandKey } from '../../utils/globalState';
import { useGetRecipes } from '../../apollo/queries/recipes.recipe.graphql';
import {
  useSearchArticleCount,
  EXPORT_SEARCH_ARTICLES,
} from '../../apollo/queries/searchArticles.web.graphql';
import { FiltersDrawer } from './FiltersDrawer';
import {
  Filters,
  filtersAreDefault,
  getDefaultFilters,
  Format,
} from './FiltersDrawer/utils';
import { Graph } from './Graph';
import { useStyles } from './styles';
import { Table } from './Table';
import { ExportButton } from './ExportButton';
import { exportToCSV, ExportRowItem } from './exportUtils';
import {
  getWhere,
  ignoreArticleQuery,
  ignoreRecipeQuery,
  articleFormatToFormatMap,
  getItemPath,
} from './utils';
import { getFrontUrl } from '../../utils/getFrontUrl';

const PAGINATION = 500;

const getRecipeItemPath = (brandKey: string, recipeId: string): string => {
  return `${config.FRONT_ONE_RECIPE}/${brandKey}/edit/${recipeId}`;
};

export const DashboardPage: FC = () => {
  const [brandKey, setBrandKey] = useGlobalBrandKey();
  const [searchParams, setSearchParams] = useState<SearchParams>({});
  const [filters, setFilters] = useState<Filters>(getDefaultFilters());
  const [filtersUrl, setFiltersUrl] = useQueryParam('filters', JsonParam);
  const [brandKeyUrl, setBrandKeyUrl] = useQueryParam('brand', StringParam);
  const [searchUrl, setSearchUrl] = useQueryParam('search', StringParam);
  const graphRef = useRef(null);
  // const graphIsOnScreen = useOnScreen(graphRef);
  const classes = useStyles();
  const [loadingUrlParams, setLoadingUrlParams] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);

  // Get article count for export pagination
  const { data: articleCountData } = useSearchArticleCount(
    {
      where: getWhere(filters, searchParams, brandKey).articleWhere,
    },
    ignoreArticleQuery(filters),
  );

  const exportArticleCount = articleCountData?.searchArticleCount || PAGINATION;

  // Use lazy query for on-demand execution
  const [getExportArticles, { loading: exportArticleLoading }] = useLazyQuery<
    SearchArticlesQuery,
    SearchArticlesQueryVariables
  >(EXPORT_SEARCH_ARTICLES, { fetchPolicy: 'network-only' });

  const {
    data: exportRecipeData,
    loading: exportRecipeLoading,
    refetch: refetchExportRecipes,
  } = useGetRecipes(
    {
      first: PAGINATION,
      skip: 0,
      orderBy: [
        RecipeOrderByInput.publishedAt_DESC,
        RecipeOrderByInput.editedAt_DESC,
      ],
      where: getWhere(filters, searchParams, brandKey).recipeWhere,
    },
    ignoreRecipeQuery(filters, brandKey),
  );

  const handleExport = useCallback(async () => {
    setExportLoading(true);
    try {
      // Fetch export data only when export is triggered
      const exportArticleResult = !ignoreArticleQuery(filters)
        ? await getExportArticles({
            variables: {
              first: exportArticleCount,
              skip: 0,
              orderBy: [
                ArticleOrderingSearchInput.publishedAt_DESC,
                ArticleOrderingSearchInput.editedAt_DESC,
              ],
              where: getWhere(filters, searchParams, brandKey).articleWhere,
            },
          })
        : null;

      // Refetch recipes if needed
      if (!ignoreRecipeQuery(filters, brandKey)) {
        await refetchExportRecipes();
      }

      const exportData: ExportRowItem[] = [
        ...(exportArticleResult?.data?.searchArticles || []).map((article) => ({
          publishedAt: article.publishedAt,
          format: articleFormatToFormatMap[article.format],
          brandKey: article.brandKey,
          title: article.title,
          categories: article.categories
            .map(({ category }) => category)
            .filter(Boolean)
            .map((category) => ({
              id: category?.id || '',
              label: category?.title || '',
            })),
          tags:
            article.tags
              ?.map(({ tag }) => tag)
              .filter(Boolean)
              .map((tag) => ({
                id: tag?.id || '',
                label: tag?.title || '',
              })) || [],
          qualifiers:
            article.qualifiers
              ?.map(({ qualifier }) => qualifier)
              .filter(Boolean)
              .map((qualifier) => ({
                id: qualifier?.id || '',
                title: qualifier?.title || '',
              })) || [],
          status: article.status,
          href: getItemPath(
            articleFormatToFormatMap[article.format],
            article.brandKey,
            article.id,
          ),
          siteUrl: article.urls?.[0]
            ? `${article.urls[0].origin}${article.urls[0].path}`
            : undefined,
        })),
        ...(exportRecipeData?.recipes || []).map((recipe) => ({
          publishedAt: recipe.publishedAt,
          format: Format.Recipe,
          brandKey: recipe.brandKey,
          title: recipe.title,
          categories: recipe.recipeCategories
            .map(({ category }) => category)
            .filter(Boolean)
            .map((category) => ({
              id: category?.id || '',
              label: category?.title || '',
            })),
          tags: [],
          qualifiers: [],
          status: recipe.status,
          href: getRecipeItemPath(recipe.brandKey, recipe.id),
          siteUrl: getFrontUrl(recipe),
        })),
      ];

      const filename = `articles_${brandKey || 'toutes'}_${
        new Date().toISOString().split('T')[0]
      }.csv`;
      exportToCSV(exportData, filename);
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setExportLoading(false);
    }
  }, [
    filters,
    brandKey,
    searchParams,
    exportArticleCount,
    getExportArticles,
    refetchExportRecipes,
    exportRecipeData,
  ]);

  useOnMount(() => {
    const startDate = parseISO(filtersUrl?.startDate);
    const endDate = parseISO(filtersUrl?.endDate);

    filtersUrl &&
      setFilters({
        ...filtersUrl,
        startDate: isValid(startDate) ? startDate : undefined,
        endDate: isValid(endDate) ? endDate : undefined,
      } as Filters);
    brandKeyUrl && setBrandKey(brandKeyUrl as BrandKey);
    searchUrl && setSearchParams((prev) => ({ ...prev, search: searchUrl }));
    setTimeout(() => setLoadingUrlParams(false));
  });

  useEffect(() => {
    setBrandKeyUrl(brandKey, 'replaceIn');
  }, [brandKey, setBrandKeyUrl]);

  useEffect(() => {
    setSearchUrl(searchParams.search, 'replaceIn');
  }, [searchParams.search, setSearchUrl]);

  useEffect(() => {
    setFiltersUrl(
      filtersAreDefault(filters) ? undefined : filters,
      'replaceIn',
    );
  }, [filters, setFiltersUrl]);

  return (
    <>
      <HomeAppBar
        appDrawerMenu={<OneDrawerMenu app={AppName.Dashboard} />}
        brandMenu={
          <BrandSwitch
            brandKey={brandKey}
            setBrandKey={(brand) => {
              setBrandKey(brand);
              setFilters((prev) => ({
                ...prev,
                tagIds: undefined,
                sourceIds: undefined,
                categoryIds: undefined,
                qualifierIds: undefined,
              }));
            }}
          />
        }
        currentUser={auth.user}
        disconnectUser={auth.logout}
        searchBar={
          <SearchBar
            placeholder="Rechercher"
            setSearchParams={setSearchParams}
            searchParams={searchParams}
          />
        }
        additionalElement={
          <ExportButton
            filters={filters}
            searchParams={searchParams}
            onExport={handleExport}
            loading={
              exportLoading || exportArticleLoading || exportRecipeLoading
            }
          />
        }
      />
      <PageWithDrawer
        fullWidth
        leftDrawer={
          <FiltersDrawer
            filters={filters}
            setFilters={setFilters}
            searchParams={searchParams}
          />
        }
        className={classes.wrapper}
      >
        <Graph
          ref={graphRef}
          filters={filters}
          searchParams={searchParams}
          loadingUrlParams={loadingUrlParams}
        />
        {!loadingUrlParams && (
          <Table filters={filters} searchParams={searchParams} />
        )}
      </PageWithDrawer>
    </>
  );
};
